// src/App.tsx — TrafficProcure MCBCE-K
// Multi-user: A_DEVELOPER · B_PM · B_PC · C_PROCUREMENT · D_FIELD
// Commercial Module Gap Analysis — CM-01 CM-02 CM-03 CM-04 CM-07 CM-08 CM-09 CM-11 implemented
// Independent AI Agents — IndependentAgentsTab added (pre-Neon/Cloudflare mock mode)
// Sprint 1: ScrollPositionProvider wraps the tree; main scroll container carries id="main-scroll"

import { useState, useEffect, useRef, useCallback } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore } from './stores/auth.store'
import { initialsFrom, tierLabel } from './stores/auth.store'
import { Dashboard }      from './pages/Dashboard'
import { Jobs }           from './pages/Jobs'
import { PurchaseOrders } from './pages/PurchaseOrders'
import { Catalogue }      from './pages/Catalogue'
import { Employees }      from './pages/Employees'
import { Timesheets }     from './pages/Timesheets'
import { PlantEquipment } from './pages/PlantEquipment'
import { ResourceMatrix } from './pages/ResourceMatrix'
import { SWMS }           from './pages/SWMS'
import { Incidents }      from './pages/Incidents'
import { SiteDiary }      from './pages/SiteDiary'
import { ITP }            from './pages/ITP'
import { Environmental }  from './pages/Environmental'
import { Inductions }     from './pages/Inductions'
import { WHSInspections } from './pages/WHSInspections'
import { Permits }        from './pages/Permits'
import { Toolbox }        from './pages/Toolbox'
import { SubcontractorWHS } from './pages/SubcontractorWHS'
import { WHSCommandCentre }      from './pages/WHSCommandCentre'
import { WHSManagementPlan }     from './pages/WHSManagementPlan'
import { WHSComplianceRegister } from './pages/WHSComplianceRegister'
import { SDSRegister }           from './pages/SDSRegister'
import { EmergencyResponsePlan } from './pages/EmergencyResponsePlan'
import { WHSAnalytics }          from './pages/WHSAnalytics'
import { SiteHazardRegister }    from './pages/SiteHazardRegister'
import { ExternalNotifications } from './pages/ExternalNotifications'
import { WHSReturnToWork }       from './pages/WHSReturnToWork'
import { WHSCompetencyMatrix }   from './pages/WHSCompetencyMatrix'
import { WHSCoverageMapping }    from './pages/WHSCoverageMapping'
import { WHSArchive }            from './pages/WHSArchive'
import { Take5JHA }              from './pages/Take5JHA'
import { FatigueManagement }     from './pages/FatigueManagement'
import { Subcontracts }   from './pages/Subcontracts'
import { Estimating }       from './pages/Estimating'
import { Contracts }        from './pages/Contracts'
import { Variations }       from './pages/Variations'
import { LiveBudget }       from './pages/LiveBudget'
import { SpendAnalytics }   from './pages/SpendAnalytics'
import { XeroIntegration }  from './pages/XeroIntegration'
import { RFQPortal }        from './pages/RFQPortal'
import { HVSwitching }      from './pages/HVSwitching'
import { ITSCommissioning } from './pages/ITSCommissioning'
import { Handover }         from './pages/Handover'
import { AICompliance }     from './pages/AICompliance'
import { Reports }          from './pages/Reports'
import { ArchiveCentre }    from './pages/ArchiveCentre'
import { Travel }           from './pages/Travel'
import { Drawings }         from './pages/Drawings'
import { Suppliers }        from './pages/Suppliers'
import { MobileComms }      from './pages/MobileComms'
import { WorkInstructions } from './pages/WorkInstructions'
import { ProgressPaymentClaims } from './pages/ProgressPaymentClaims'
import { CostCodes }        from './pages/CostCodes'
import { Roster }           from './pages/Roster'
import { Workforce }        from './pages/Workforce'
import { SettingsPage }        from './pages/Settings'
import { DataSourceSettings }  from './pages/DataSourceSettings'
import { WHSResponsibilityStatement } from './pages/WHSResponsibilityStatement'
import { WHSTrafficManagementPlan }  from './pages/WHSTrafficManagementPlan'
import { WHSSiteRiskAssessment }     from './pages/WHSSiteRiskAssessment'
import { PlantPreStartChecks }       from './pages/PlantPreStartChecks'
import { PlantWorkOrders }           from './pages/PlantWorkOrders'
import { SupplierIntelligenceAgent } from './pages/SupplierIntelligenceAgent'
import { LicenseSettings }          from './pages/LicenseSettings'
import { IndependentAgentsTab } from './pages/IndependentAgentsTab'
import { MOCK_MILESTONES } from './utils/mockDataClaims'
import { MOCK_TENDERS, getActiveTenders } from './utils/mockDataEstimating'
import { MOCK_CONTRACTS } from './utils/mockDataContracts'
import { MOCK_VARIATIONS, getVariationNoticeDaysLeft, getNoticeUrgency } from './utils/mockDataCommercial'
import { MOCK_RFQS } from './utils/mockDataRFQ'
import { getCostCodeAlertCount } from './utils/mockDataCostCodes'
import { MOBILE_SYNC_SUMMARY } from './utils/mockDataMobile'
import { MOCK_WORKER_INDUCTIONS, MOCK_WHS_INSPECTIONS, MOCK_PERMITS, MOCK_WHS_NOTIFICATIONS } from './utils/mockDataWHS'
import { MOCK_CARS } from './utils/mockDataWHSMgmt'
import { MOCK_EQUIPMENT, getOpenEquipmentDefects } from './utils/mockDataExtended'
import {
  LayoutDashboard, Briefcase, Package, Truck, ShoppingCart,
  Users, Settings, LogOut, Menu, Shield, ClipboardList, BarChart2, BarChart3,
  FileSearch, AlertTriangle, ChevronDown, Zap, Radio, Wrench,
  Calendar, DollarSign, MapPin, Activity, BookOpen, CheckSquare, Layers,
  FileText, Smartphone, Link2, Send, ClipboardCheck, Bell, Building2, Archive,
  FlameKindling, BookMarked, Moon, TrendingUp, Bot, Tag, Heart, Database, Sparkles, GripVertical, Lock, Unlock,
} from 'lucide-react'
import { getArchiveRegister } from './utils/archiveRegistry'
import { isTestMode, getTestModeInfo } from './utils/platformMode'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ClerkAuthGate } from './components/ClerkAuthGate'
import { useClerk } from '@clerk/clerk-react'
import { ScrollPositionProvider } from './utils/ScrollPositionContext'

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30_000 } } })

function ComingSoon({ title, icon: Icon }: { title: string; icon: any }) {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center text-gray-400">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4"><Icon className="w-7 h-7 text-gray-400"/></div>
        <div className="font-semibold text-gray-600 text-lg">{title}</div>
        <div className="text-sm mt-1">Module coming in next build step</div>
      </div>
    </div>
  )
}

const DEMO_ACCOUNTS = [
  { tier:'A_DEVELOPER', label:'Tier A — Developer',                bg:'bg-purple-50', border:'border-purple-200', text:'text-purple-700', users:[{ email:'admin@trafficprocure.local', password:'Admin1234!', name:'System Admin' }] },
  { tier:'B_PM',        label:'Tier B — Project Manager (all jobs)',bg:'bg-blue-50',   border:'border-blue-200',   text:'text-blue-700',   users:[{ email:'pm@trafficprocure.local',  password:'PM1234!pm',   name:'Jane Smith' },{ email:'pm2@trafficprocure.local', password:'PM1234!pm', name:'Robert Chen' }] },
  { tier:'B_PC',        label:'Tier B — Project Coordinator',       bg:'bg-sky-50',    border:'border-sky-200',    text:'text-sky-700',    users:[{ email:'pc@trafficprocure.local',  password:'PC1234!pc',   name:'Sarah Lee — j1/j2/j6' },{ email:'pc2@trafficprocure.local', password:'PC1234!pc', name:'Mike Walsh — j3/j4/j5' }] },
  { tier:'C_WHSO',        label:'Tier C — WHS Officer',              bg:'bg-orange-50', border:'border-orange-200', text:'text-orange-700', users:[{ email:'whso@trafficprocure.local', password:'WHSO1234!', name:'Alex Morgan' }] },
  { tier:'C_PROCUREMENT', label:'Tier C — Procurement',              bg:'bg-teal-50',   border:'border-teal-200',   text:'text-teal-700',   users:[{ email:'proc@trafficprocure.local', password:'Proc1234!', name:'Mark Wilson' }] },
  { tier:'D_FIELD',     label:'Tier D — Field / Mobile',            bg:'bg-gray-50',   border:'border-gray-200',   text:'text-gray-700',   users:[{ email:'field@trafficprocure.local', password:'Field123!', name:'Tony Rivera' }] },
]

function LoginPage() {
  const { login, isLoading, error, requiresMfa, clearError } = useAuthStore()
  const [email, setEmail] = useState(''); const [password, setPassword] = useState(''); const [mfaToken, setMfaToken] = useState(''); const [showHint, setShowHint] = useState(false)
  const handleSubmit = async (e: React.FormEvent) => { e.preventDefault(); await login(email, password, requiresMfa ? mfaToken : undefined) }
  const fillCredential = (e: string, p: string) => { setEmail(e); setPassword(p); clearError() }
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1F3864] to-[#2E75B6] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8"><div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4"><Zap className="w-8 h-8 text-white"/></div><h1 className="text-3xl font-bold text-white">TrafficProcure</h1><p className="text-blue-200 mt-1 text-sm">Civil &amp; Electrical Operations Platform</p></div>
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{requiresMfa ? 'Two-factor authentication' : 'Sign in'}</h2>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2"><AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0"/><p className="text-sm text-red-700">{error}</p></div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!requiresMfa ? (<><div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" value={email} autoFocus required onChange={e=>{setEmail(e.target.value);clearError()}} className="input" placeholder="email@trafficprocure.local"/></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Password</label><input type="password" value={password} required onChange={e=>{setPassword(e.target.value);clearError()}} className="input"/></div></>) : (<div><label className="block text-sm font-medium text-gray-700 mb-1">Authenticator code</label><p className="text-xs text-gray-500 mb-2">Enter any 6-digit code for this demo.</p><input type="text" value={mfaToken} maxLength={6} autoFocus required onChange={e=>setMfaToken(e.target.value)} className="input text-center tracking-widest text-lg font-mono" placeholder="000000"/></div>)}
            <button type="submit" disabled={isLoading} className="btn-primary w-full py-2.5">{isLoading ? 'Signing in…' : 'Sign in'}</button>
          </form>
          <div className="mt-6 pt-5 border-t"><button onClick={()=>setShowHint(!showHint)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"><ChevronDown className={`w-3 h-3 transition-transform ${showHint?'rotate-180':''}`}/>Demo credentials</button>
          {showHint && <div className="mt-3 space-y-3">{DEMO_ACCOUNTS.map(a=><div key={a.tier} className={`rounded-xl border ${a.border} ${a.bg} p-3`}><p className={`text-xs font-semibold ${a.text} mb-2`}>{a.label}</p><div className="space-y-1">{a.users.map(u=><button key={u.email} onClick={()=>fillCredential(u.email,u.email.split('@')[0]==='admin'?'Admin1234!':u.email.split('@')[0]==='pm'||u.email.split('@')[0]==='pm2'?'PM1234!pm':u.email.split('@')[0]==='pc'||u.email.split('@')[0]==='pc2'?'PC1234!pc':u.email.split('@')[0]==='proc'?'Proc1234!':'Field123!')} className={`block w-full text-left text-xs ${a.text} hover:underline`}>{u.name} — {u.email}</button>)}</div></div>)}</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

function NavGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-300/60 px-2 mb-1">{label}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  )
}

function NavItem({ icon: Icon, label, active, onClick, badge }: {
  icon: any; label: string; active: boolean; onClick: () => void; badge?: number
}) {
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left transition-colors text-sm ${active ? 'bg-white/15 text-white font-medium' : 'text-blue-100/80 hover:bg-white/10 hover:text-white'}`}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge != null && badge > 0 && <span className="bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0">{badge > 9 ? '9+' : badge}</span>}
    </button>
  )
}

// Collapsible WHS sub-group — WHS v2.0 nav condensation
function NavAccordion({ icon: Icon, label, isOpen, onToggle, badge, hasActive, children }: {
  icon: any; label: string; isOpen: boolean; onToggle: () => void;
  badge?: number; hasActive?: boolean; children: React.ReactNode
}) {
  return (
    <div>
      <button onClick={onToggle}
        className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left transition-colors text-sm ${
          hasActive && !isOpen ? 'bg-white/10 text-white font-medium'
          : isOpen ? 'text-blue-200 hover:text-white'
          : 'text-blue-100/80 hover:bg-white/10 hover:text-white'
        }`}>
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 truncate text-xs font-medium uppercase tracking-wide">{label}</span>
        {badge != null && badge > 0 && !isOpen && (
          <span className="bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0">{badge > 9 ? '9+' : badge}</span>
        )}
        <ChevronDown className={`w-3 h-3 flex-shrink-0 text-blue-300/70 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="ml-3 mt-0.5 pl-2 border-l border-white/10 space-y-0.5 pb-1">
          {children}
        </div>
      )}
    </div>
  )
}

function AppShell() {
  const { user, logout, isTier, canAssignJobs } = useAuthStore()
  const [page, setPage]             = useState('dashboard')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [testMode, setTestMode]     = useState(isTestMode)

  // ─ Sidebar resize ────────────────────────────────────────────────────────────────
  const MIN_SIDEBAR_W     = 160
  const MAX_SIDEBAR_W     = 380
  const DEFAULT_SIDEBAR_W = 224 // Tailwind w-56
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('tp-sidebar-width')
      return stored ? Math.min(MAX_SIDEBAR_W, Math.max(MIN_SIDEBAR_W, parseInt(stored, 10))) : DEFAULT_SIDEBAR_W
    } catch { return DEFAULT_SIDEBAR_W }
  })
  const draggingRef = useRef(false)

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    draggingRef.current = true
    const startX = e.clientX
    const startW = sidebarWidth
    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return
      const newW = Math.min(MAX_SIDEBAR_W, Math.max(MIN_SIDEBAR_W, startW + ev.clientX - startX))
      setSidebarWidth(newW)
    }
    const onUp = () => {
      draggingRef.current = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      setSidebarWidth(prev => { try { localStorage.setItem('tp-sidebar-width', String(prev)) } catch{} return prev })
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [sidebarWidth])

  // ─ Sidebar scroll lock ────────────────────────────────────────────────────────
  const [scrollLocked, setScrollLocked] = useState<boolean>(() => {
    try { return localStorage.getItem('tp-sidebar-scroll-locked') === 'true' } catch { return false }
  })
  const navScrollRef    = useRef<HTMLDivElement>(null)
  const savedScrollTop  = useRef(0)

  // When scroll is locked, restore the saved scroll position after any page change
  useEffect(() => {
    if (scrollLocked && navScrollRef.current) {
      navScrollRef.current.scrollTop = savedScrollTop.current
    }
  }, [page, scrollLocked])

  const toggleScrollLock = useCallback(() => {
    setScrollLocked(prev => {
      const next = !prev
      try { localStorage.setItem('tp-sidebar-scroll-locked', String(next)) } catch{}
      // When locking, save current position; when unlocking, allow free scroll
      if (!prev && navScrollRef.current) savedScrollTop.current = navScrollRef.current.scrollTop
      return next
    })
  }, [])

  // Listen for test mode changes dispatched by Settings (same-tab custom event)
  useEffect(() => {
    const handler = (e: CustomEvent) => setTestMode(e.detail.mode === 'TEST')
    window.addEventListener('tp-mode-changed', handler as EventListener)
    return () => window.removeEventListener('tp-mode-changed', handler as EventListener)
  }, [])

  // tp-navigate: allows any page or button to drive top-level navigation
  // without needing prop drilling or a global store.
  // Usage: document.dispatchEvent(new CustomEvent('tp-navigate', { detail: 'jobs' }))
  useEffect(() => {
    const handler = (e: Event) => {
      const target = (e as CustomEvent).detail as string
      if (target) { setPage(target); setMobileOpen(false) }
    }
    document.addEventListener('tp-navigate', handler)
    return () => document.removeEventListener('tp-navigate', handler)
  }, []) // setPage and setMobileOpen are stable useState dispatchers

  const go      = (p: string) => { setPage(p); setMobileOpen(false) }
  const isPM    = isTier('B_PM')
  const isPC    = isTier('B_PC')
  const isWHSO  = user?.tier === 'C_WHSO'   // identity check — not access check
  const isProc  = isTier('C_PROCUREMENT')
  const isAdmin = isTier('A_DEVELOPER')

  // WHS nav accordion state — which of the 5 groups is currently open
  // Auto-opens the group containing the active page on first render
  const WHS_GROUPS: Record<string, string[]> = {
    command:   ['whs-command', 'whs-analytics'],
    docs:      ['whs-plan', 'swms', 'permits', 'sds-register', 'erp', 'whs-compliance', 'whs-archive'],
    field:     ['whs-sra', 'whs-tmp', 'take5', 'inspections', 'fatigue', 'site-diary'],
    people:    ['inductions', 'whs-rs', 'whs-rtw', 'whs-competency', 'whs-coverage', 'sub-whs'],
    registers: ['incidents', 'hazard-register', 'environmental', 'external-notif', 'toolbox'],
  }
  const getActiveWhsGroup = (p: string) => Object.entries(WHS_GROUPS).find(([, pages]) => pages.includes(p))?.[0] ?? null
  const [whsOpen, setWhsOpen] = useState<string | null>(() => getActiveWhsGroup('whs-command'))
  const toggleWhs = (key: string) => setWhsOpen(prev => prev === key ? null : key)

  // ── Badge counts ──────────────────────────────────────────────
  const pendingInductions = MOCK_WORKER_INDUCTIONS.filter(i => i?.status === 'PENDING').length
  const openIssues        = MOCK_WHS_INSPECTIONS.flatMap(i => (i as any)?.findings ?? []).filter(f => f?.status === 'OPEN').length
  const pendingPermits    = MOCK_PERMITS.filter(p => p?.status === 'PENDING').length
  const overdueCARs       = MOCK_CARS.filter(c => c?.status === 'OPEN' && new Date(c?.dueDate ?? 0) < new Date()).length
  const unreadNotifs      = MOCK_WHS_NOTIFICATIONS.filter(n => n && n.acknowledgedCount < n.deliveredCount).length
  const archivePackCount  = getArchiveRegister().filter(a => (a as any)?.status === 'PENDING').length
  const varAlertCount     = MOCK_VARIATIONS.filter(v =>
    v && !v.noticeIssuedDate && v.status !== 'APPROVED' &&
    ['URGENT','WARN','MISSED'].includes(getNoticeUrgency(getVariationNoticeDaysLeft(v)))
  ).length

  const renderPage = () => {
    switch (page) {
      case 'dashboard':         return <Dashboard onNavigate={go}/>
      case 'jobs':              return <Jobs/>
      case 'pos':               return <PurchaseOrders/>
      case 'catalogue': case 'stock': return <Catalogue/>
      case 'employees': case 'timesheets': case 'roster': case 'workforce': return <Workforce/>
      case 'plant':             return <PlantEquipment/>
      case 'resource-matrix':   return <ResourceMatrix/>
      case 'inductions':        return <Inductions/>
      case 'swms':              return <SWMS/>
      case 'permits':           return <Permits/>
      case 'inspections':       return <WHSInspections/>
      case 'toolbox':           return <Toolbox/>
      case 'sub-whs':           return <SubcontractorWHS/>
      case 'incidents':         return <Incidents/>
      case 'environmental':     return <Environmental/>
      case 'whs-rtw':          return <WHSReturnToWork/>
      case 'whs-competency':  return <WHSCompetencyMatrix/>
      case 'whs-coverage':    return <WHSCoverageMapping/>
      case 'whs-command':       return <WHSCommandCentre/>
      case 'whs-plan':          return <WHSManagementPlan/>
      case 'whs-compliance':    return <WHSComplianceRegister/>
      case 'sds-register':      return <SDSRegister/>
      case 'erp':               return <EmergencyResponsePlan/>
      case 'whs-analytics':     return <WHSAnalytics/>
      case 'hazard-register':   return <SiteHazardRegister/>
      case 'external-notif':    return <ExternalNotifications/>
      case 'whs-archive':       return <WHSArchive/>
      case 'take5':             return <Take5JHA/>
      case 'fatigue':           return <FatigueManagement/>
      case 'site-diary':        return <SiteDiary/>
      case 'itp':               return <ITP/>
      case 'estimating':        return (isPC||isPM) ? <Estimating/>             : <ComingSoon title="Estimating"      icon={DollarSign}/>
      case 'contracts':         return (isPC||isPM) ? <Contracts/>              : <ComingSoon title="Contracts"        icon={FileText}/>
      case 'variations':        return (isPC||isPM) ? <Variations/>             : <ComingSoon title="Variations"       icon={AlertTriangle}/>
      case 'live-budget':       return (isPC||isPM) ? <LiveBudget/>             : <ComingSoon title="Live Budget"      icon={TrendingUp}/>
      case 'spend-analytics':   return (isProc||isPC||isPM) ? <SpendAnalytics/>  : <ComingSoon title="Spend Analytics"  icon={BarChart3}/>
      case 'xero':              return (isPM||isAdmin) ? <XeroIntegration/>     : <ComingSoon title="Xero Integration" icon={Link2}/>
      case 'rfq':               return (isProc||isPC||isPM) ? <RFQPortal/>      : <ComingSoon title="RFQ Portal"       icon={Send}/>
      case 'claims':            return (isPC||isPM) ? <ProgressPaymentClaims/>   : <ComingSoon title="Progress Claims"  icon={DollarSign}/>
      case 'subcontracts':      return <Subcontracts/>
      case 'cost-codes':        return (isPM||isAdmin) ? <CostCodes/> : <ComingSoon title="Cost Code Libraries" icon={Tag}/>
      case 'hv-switching':      return <HVSwitching/>
      case 'its-comms':         return <ITSCommissioning/>
      case 'handover':          return <Handover/>
      case 'qa-ncr':            return <ITP/>
      case 'reports':           return <Reports/>
      case 'archive-centre':    return <ArchiveCentre/>
      case 'compliance':        return <AICompliance/>
      case 'ai-agents':         return <IndependentAgentsTab/>
      case 'travel':            return <Travel/>
      case 'settings':          return <SettingsPage/>
      case 'datasource':        return isAdmin ? <DataSourceSettings/> : <ComingSoon title="Data Source Settings" icon={Database}/>
      case 'whs-rs':            return <WHSResponsibilityStatement/>
      case 'whs-tmp':           return <WHSTrafficManagementPlan/>
      case 'whs-sra':           return <WHSSiteRiskAssessment/>
      case 'plant-prestart':     return <PlantPreStartChecks/>
      case 'plant-workorders':   return <PlantWorkOrders/>
      case 'supplier-intel':     return <SupplierIntelligenceAgent/>
      case 'license':            return isAdmin ? <LicenseSettings/> : <ComingSoon title="License" icon={Shield}/>
      case 'mobile':            return <MobileComms/>
      case 'works-instr':       return <WorkInstructions/>
      case 'suppliers':         return <Suppliers/>
      case 'drawings':          return <Drawings/>
      default:                  return <ComingSoon title={page} icon={Wrench}/>
    }
  }

  const urgentCount = (MOBILE_SYNC_SUMMARY?.rejectedAttendance ?? 0) + (MOBILE_SYNC_SUMMARY?.openWHSSubmissions ?? 0) + (MOBILE_SYNC_SUMMARY?.criticalWHSAlerts ?? 0)
  const whsBadge    = overdueCARs + (MOCK_WHS_NOTIFICATIONS.filter(n => n && n.acknowledgedCount < n.deliveredCount).length)

  const rfqBadge      = MOCK_RFQS.filter(r => r?.status === 'RESPONSES_RECEIVED').length || undefined
  const contractBadge = MOCK_CONTRACTS.filter(c => c && ['EXPIRING_SOON','EXPIRED'].includes(c.status)).length || undefined
  const milstoneBadge = MOCK_MILESTONES.filter(m => m?.status === 'ELIGIBLE').length
  const costCodeBadge = getCostCodeAlertCount() || undefined

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-[#1F3864] flex-shrink-0 relative select-none" style={{ width: sidebarWidth }}>
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#2E75B6] rounded-lg flex items-center justify-center flex-shrink-0"><Zap className="w-4 h-4 text-white"/></div>
          <div className="flex-1 min-w-0"><div className="font-bold text-white text-sm truncate">TrafficProcure</div><div className="text-blue-300 text-xs">Civil &amp; Electrical</div></div>
          {/* Scroll lock toggle */}
          <button
            onClick={toggleScrollLock}
            title={scrollLocked ? 'Scroll locked — click to unlock' : 'Click to lock sidebar scroll position'}
            className={`p-1.5 rounded-lg flex-shrink-0 transition-colors ${
              scrollLocked ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30' : 'text-blue-400/50 hover:text-blue-200 hover:bg-white/10'
            }`}
          >
            {scrollLocked ? <Lock className="w-3 h-3"/> : <Unlock className="w-3 h-3"/>}
          </button>
        </div>
      </div>

      <nav
        ref={navScrollRef}
        className="flex-1 overflow-y-auto p-3 space-y-4"
        onScroll={e => { if (!scrollLocked) savedScrollTop.current = (e.target as HTMLDivElement).scrollTop }}
      >
        <NavGroup label="Overview">
          <NavItem icon={LayoutDashboard} label="Dashboard" active={page==='dashboard'} onClick={()=>go('dashboard')}/>
        </NavGroup>

        <NavGroup label="Projects">
          <NavItem icon={Briefcase}     label="Jobs & sites"       active={page==='jobs'}        onClick={()=>go('jobs')}/>
          <NavItem icon={FileSearch}    label="Drawings & specs"   active={page==='drawings'}     onClick={()=>go('drawings')}/>
          <NavItem icon={BookOpen}      label="Works instructions" active={page==='works-instr'}  onClick={()=>go('works-instr')}/>
          <NavItem icon={CheckSquare}   label="ITP & hold points"  active={page==='itp'}          onClick={()=>go('itp')}/>
          <NavItem icon={ClipboardList} label="Site diary"         active={page==='site-diary'}   onClick={()=>go('site-diary')}/>
        </NavGroup>

        <NavGroup label="People & Resources">
          <NavItem icon={LayoutDashboard} label="Workforce"       active={['workforce','roster','employees','timesheets'].includes(page)} onClick={()=>go('workforce')}/>
          <NavItem icon={Truck}         label="Plant & fleet"     active={page==='plant'}           onClick={()=>go('plant')}/>
          <NavItem icon={ClipboardList} label="Pre-start checks"  active={page==='plant-prestart'}  onClick={()=>go('plant-prestart')} badge={MOCK_EQUIPMENT.filter(e=>getOpenEquipmentDefects(e.id).length>0).length||undefined}/>
          <NavItem icon={Wrench}        label="Work orders"       active={page==='plant-workorders'} onClick={()=>go('plant-workorders')} badge={getOpenEquipmentDefects().length||undefined}/>
          <NavItem icon={Layers}        label="Resource matrix"   active={page==='resource-matrix'} onClick={()=>go('resource-matrix')}/>
          <NavItem icon={MapPin}        label="Travel & accom"    active={page==='travel'}          onClick={()=>go('travel')} badge={2}/>
          <NavItem icon={Smartphone}    label="Mobile & crew"     active={page==='mobile'}          onClick={()=>go('mobile')} badge={urgentCount>0?urgentCount:undefined}/>
        </NavGroup>

        {(isProc||isAdmin) && (
          <NavGroup label="Procurement">
            <NavItem icon={ShoppingCart} label="Purchase orders"    active={page==='pos'}              onClick={()=>go('pos')}/>
            <NavItem icon={Package}      label="Catalogue"          active={page==='catalogue'}         onClick={()=>go('catalogue')}/>
            <NavItem icon={Users}        label="Suppliers"          active={page==='suppliers'}         onClick={()=>go('suppliers')}/>
            <NavItem icon={Sparkles}     label="Supplier intel"     active={page==='supplier-intel'}    onClick={()=>go('supplier-intel')} badge={2}/>
            <NavItem icon={Send}         label="RFQ portal"         active={page==='rfq'}               onClick={()=>go('rfq')} badge={rfqBadge}/>
            <NavItem icon={BarChart3}    label="Spend analytics"    active={page==='spend-analytics'}   onClick={()=>go('spend-analytics')}/>
          </NavGroup>
        )}

        <NavGroup label="Safety & Env">
          {/* WHS v2.0 condensation: 25 flat items → 5 collapsible groups */}

          {/* 1 — WHS Command (top-level hub, always visible) */}
          <NavItem icon={Activity}    label="WHS command centre" active={page==='whs-command'} onClick={()=>{go('whs-command');setWhsOpen('command')}} badge={whsBadge>0?whsBadge:undefined}/>

          {/* 2 — Safety Documents */}
          <NavAccordion icon={FileText} label="Safety documents"
            isOpen={whsOpen==='docs'} onToggle={()=>toggleWhs('docs')}
            badge={pendingPermits||undefined}
            hasActive={WHS_GROUPS.docs.includes(page)}>
            <NavItem icon={Shield}        label="WHS management plan"    active={page==='whs-plan'}       onClick={()=>go('whs-plan')}/>
            <NavItem icon={Shield}        label="SWMS"                   active={page==='swms'}           onClick={()=>go('swms')}/>
            <NavItem icon={FileText}      label="Permits to Work"        active={page==='permits'}        onClick={()=>go('permits')} badge={pendingPermits||undefined}/>
            <NavItem icon={FlameKindling} label="SDS register"           active={page==='sds-register'}   onClick={()=>go('sds-register')}/>
            <NavItem icon={AlertTriangle} label="Emergency plan"         active={page==='erp'}            onClick={()=>go('erp')}/>
            <NavItem icon={BookMarked}    label="Compliance register"    active={page==='whs-compliance'} onClick={()=>go('whs-compliance')}/>
            <NavItem icon={Archive}       label="WHS archive"            active={page==='whs-archive'}    onClick={()=>go('whs-archive')}/>
          </NavAccordion>

          {/* 3 — Field Safety */}
          <NavAccordion icon={Shield} label="Field safety"
            isOpen={whsOpen==='field'} onToggle={()=>toggleWhs('field')}
            badge={(openIssues||0)+1+1||undefined}
            hasActive={WHS_GROUPS.field.includes(page)}>
            <NavItem icon={AlertTriangle}  label="Site risk assessments" active={page==='whs-sra'}       onClick={()=>go('whs-sra')} badge={1}/>
            <NavItem icon={Truck}          label="Traffic Mgmt Plans"    active={page==='whs-tmp'}       onClick={()=>go('whs-tmp')} badge={1}/>
            <NavItem icon={Shield}         label="Take 5 / JHA"          active={page==='take5'}         onClick={()=>go('take5')} badge={1}/>
            <NavItem icon={ClipboardList}  label="WHS inspections"       active={page==='inspections'}   onClick={()=>go('inspections')} badge={openIssues||undefined}/>
            <NavItem icon={Moon}           label="Fatigue management"    active={page==='fatigue'}       onClick={()=>go('fatigue')}/>
            <NavItem icon={ClipboardList}  label="Site diary"            active={page==='site-diary'}    onClick={()=>go('site-diary')}/>
          </NavAccordion>

          {/* 4 — People & Compliance */}
          <NavAccordion icon={Users} label="People & compliance"
            isOpen={whsOpen==='people'} onToggle={()=>toggleWhs('people')}
            badge={(pendingInductions+3+2+3)||undefined}
            hasActive={WHS_GROUPS.people.includes(page)}>
            <NavItem icon={ClipboardCheck} label="Inductions"            active={page==='inductions'}     onClick={()=>go('inductions')} badge={pendingInductions||undefined}/>
            <NavItem icon={FileText}       label="Responsibility stmts"  active={page==='whs-rs'}         onClick={()=>go('whs-rs')} badge={3}/>
            <NavItem icon={Heart}          label="Return to work"        active={page==='whs-rtw'}        onClick={()=>go('whs-rtw')} badge={2}/>
            <NavItem icon={ClipboardCheck} label="Competency matrix"     active={page==='whs-competency'} onClick={()=>go('whs-competency')} badge={3}/>
            <NavItem icon={CheckSquare}    label="Coverage mapping"      active={page==='whs-coverage'}   onClick={()=>go('whs-coverage')} badge={8}/>
            <NavItem icon={Building2}      label="Subcontractors WHS"    active={page==='sub-whs'}        onClick={()=>go('sub-whs')}/>
          </NavAccordion>

          {/* 5 — Registers & Risk */}
          <NavAccordion icon={BarChart3} label="Registers & risk"
            isOpen={whsOpen==='registers'} onToggle={()=>toggleWhs('registers')}
            badge={((overdueCARs||2)+unreadNotifs+1)||undefined}
            hasActive={WHS_GROUPS.registers.includes(page)}>
            <NavItem icon={AlertTriangle}  label="Incidents & CAR"       active={page==='incidents'}      onClick={()=>go('incidents')} badge={overdueCARs||2}/>
            <NavItem icon={Bell}           label="Toolbox & alerts"       active={page==='toolbox'}        onClick={()=>go('toolbox')} badge={unreadNotifs||undefined}/>
            <NavItem icon={Layers}         label="Site hazard register"  active={page==='hazard-register'} onClick={()=>go('hazard-register')}/>
            <NavItem icon={Activity}       label="Environmental"         active={page==='environmental'}  onClick={()=>go('environmental')}/>
            <NavItem icon={Send}           label="External notifications" active={page==='external-notif'} onClick={()=>go('external-notif')} badge={1}/>
            <NavItem icon={BarChart3}      label="WHS analytics"         active={page==='whs-analytics'}  onClick={()=>go('whs-analytics')}/>
          </NavAccordion>
        </NavGroup>

        {(isPM||isPC||isAdmin) && !isWHSO && (
          <NavGroup label="Commercial">
            <NavItem icon={FileSearch}    label="Estimating"      active={page==='estimating'}     onClick={()=>go('estimating')} badge={getActiveTenders().length||undefined}/>
            <NavItem icon={FileText}      label="Contracts"       active={page==='contracts'}       onClick={()=>go('contracts')} badge={contractBadge}/>
            <NavItem icon={AlertTriangle} label="Variations"      active={page==='variations'}      onClick={()=>go('variations')} badge={varAlertCount||undefined}/>
            <NavItem icon={TrendingUp}    label="Live budget"     active={page==='live-budget'}     onClick={()=>go('live-budget')}/>
            <NavItem icon={Send}          label="RFQ portal"      active={page==='rfq'}             onClick={()=>go('rfq')} badge={rfqBadge}/>
            <NavItem icon={BarChart3}     label="Spend analytics" active={page==='spend-analytics'} onClick={()=>go('spend-analytics')}/>
            <NavItem icon={DollarSign}    label="Progress claims" active={page==='claims'}          onClick={()=>go('claims')} badge={milstoneBadge||undefined}/>
            <NavItem icon={Building2}     label="Subcontracts"    active={page==='subcontracts'}    onClick={()=>go('subcontracts')}/>
            <NavItem icon={Tag}           label="Cost codes"      active={page==='cost-codes'}      onClick={()=>go('cost-codes')} badge={costCodeBadge}/>
          </NavGroup>
        )}

        <NavGroup label="Specialist">
          <NavItem icon={Zap}   label="HV / Switching"    active={page==='hv-switching'} onClick={()=>go('hv-switching')}/>
          <NavItem icon={Radio} label="ITS commissioning" active={page==='its-comms'}    onClick={()=>go('its-comms')}/>
        </NavGroup>

        <NavGroup label="Compliance & AI">
          <NavItem icon={Shield}      label="AI compliance"  active={page==='compliance'}     onClick={()=>go('compliance')}/>
          <NavItem icon={Bot}         label="AI agents"      active={page==='ai-agents'}      onClick={()=>go('ai-agents')}/>
          <NavItem icon={FileText}    label="Handover"       active={page==='handover'}       onClick={()=>go('handover')}/>
          <NavItem icon={CheckSquare} label="QA / NCR"       active={page==='qa-ncr'}         onClick={()=>go('qa-ncr')}/>
          <NavItem icon={BarChart2}   label="Reports"        active={page==='reports'}        onClick={()=>go('reports')}/>
          <NavItem icon={Archive}     label="Archive centre" active={page==='archive-centre'} onClick={()=>go('archive-centre')} badge={archivePackCount||undefined}/>
        </NavGroup>

        {(isAdmin||canAssignJobs()) && (
          <NavGroup label="Administration">
            <NavItem icon={Settings}  label="Settings & access" active={page==='settings'}    onClick={()=>go('settings')}/>
            {isAdmin && <NavItem icon={Database} label="Data source"       active={page==='datasource'} onClick={()=>go('datasource')}/>}
            {isAdmin && <NavItem icon={Shield}   label="License"           active={page==='license'}    onClick={()=>go('license')}/>}
            {(isAdmin||isPM) && <NavItem icon={Link2} label="Xero integration" active={page==='xero'} onClick={()=>go('xero')}/>}
          </NavGroup>
        )}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="w-8 h-8 rounded-full bg-[#2E75B6] flex items-center justify-center text-xs font-bold text-white flex-shrink-0">{initialsFrom(user?.fullName??'U')}</div>
          <div className="flex-1 min-w-0"><div className="text-sm font-medium text-white truncate">{user?.fullName}</div><div className="text-xs text-blue-300 truncate">{tierLabel(user?.tier??'')}</div></div>
          <button onClick={logout} className="p-1.5 text-blue-300 hover:text-white hover:bg-white/10 rounded-lg"><LogOut className="w-4 h-4"/></button>
        </div>
        {user?.tier==='B_PC'&&user.assignedJobs&&(
          <div className="mt-2 px-2 py-1.5 bg-sky-900/40 rounded-lg"><div className="text-xs text-sky-300 font-medium mb-1">Your assigned jobs</div><div className="flex flex-wrap gap-1">{user.assignedJobs.map(jId=><span key={jId} className="text-xs bg-sky-600/40 text-sky-200 px-1.5 py-0.5 rounded font-mono">{jId.replace('j','J')}</span>)}</div></div>
        )}
      </div>

      {/* Drag-to-resize handle on right edge — hover reveals grip, drag resizes */}
      <div
        onMouseDown={startResize}
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize z-20 flex items-center justify-center group"
        title="Drag to resize sidebar"
      >
        <GripVertical className="w-3 h-6 text-white/0 group-hover:text-white/30 transition-colors duration-150" />
      </div>
    </div>
  )

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <div className="hidden lg:flex"><Sidebar/></div>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden"><div className="absolute inset-0 bg-black/50" onClick={()=>setMobileOpen(false)}/><div className="absolute left-0 top-0 h-full z-50"><Sidebar/></div></div>
      )}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b"><button onClick={()=>setMobileOpen(true)}><Menu className="w-5 h-5 text-gray-600"/></button><span className="font-semibold text-gray-900">TrafficProcure</span><div className="w-5"/></div>
        {testMode && (
          <div className="bg-amber-400 px-4 py-1.5 flex items-center justify-center gap-2 text-sm font-bold text-amber-900 flex-shrink-0">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            BETA TEST MODE — {new Date().toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' })} — Data entered may be reset. Not for live payroll or client invoicing.
            {getTestModeInfo().enabledByName && (
              <span className="text-amber-700 font-normal text-xs">Enabled by {getTestModeInfo().enabledByName}</span>
            )}
          </div>
        )}
        {/* id="main-scroll" is required by ScrollPositionContext to save/restore per-page scroll */}
        <main id="main-scroll" className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6">{renderPage()}</div>
        </main>
      </div>
    </div>
  )
}

export default function App() {
  const user = useAuthStore(s=>s.user)
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ScrollPositionProvider>
          {user ? <AppShell/> : <LoginPage/>}
        </ScrollPositionProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
